import { AlertCircle, Check, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { computePostAuthRedirect } from '@/lib/post-auth-redirect';
import { useAuthStore } from '@/stores/authStore';
import { STRINGS } from '@/strings/strings';

type VerifyState = 'verifying' | 'success' | 'error';

export function VerifyTokenRoute() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  // Sebastian ruling 2026-09-17 — the book_lightgate verification
  // link carries `book_order_id` so the PWA can return the customer
  // to his book after clicking. Empty query param → fall back to the
  // default post-auth redirect.
  const bookOrderId = searchParams.get('book_order_id');
  const hasBookOrderId = bookOrderId !== null && bookOrderId !== '';
  const verifyEmail = useAuthStore((s) => s.verifyEmail);
  const user = useAuthStore((s) => s.user);
  const userEmail = user?.email ?? null;
  const navigate = useNavigate();

  const [state, setState] = useState<VerifyState>(token ? 'verifying' : 'error');
  const didRun = useRef(false);

  useEffect(() => {
    if (didRun.current || !token) return;
    didRun.current = true;

    verifyEmail({ token })
      .then(() => {
        // Sebastian ruling 2026-09-18 — book flow with a resolved
        // order skips the "E-Mail bestätigt!" confirmation screen and
        // lands directly on /book/<id>. We `navigate` BEFORE any
        // `setState('success')` so React never renders the success
        // heading — the loader stays visible while the router
        // unmounts this route. Audio flow (no book_order_id) and
        // book flow without a resolvable order both keep the
        // confirmation screen.
        if (hasBookOrderId) {
          navigate(`/book/${bookOrderId}`, { replace: true });
          return;
        }
        setState('success');
      })
      .catch(() => setState('error'));
  }, [token, verifyEmail, hasBookOrderId, bookOrderId, navigate]);

  if (state === 'verifying') {
    return (
      <div className="text-center">
        <Loader2 className="w-10 h-10 animate-spin text-dream-white mx-auto mb-4" />
        <p className="text-sm text-dream-white/80">{STRINGS.auth.verify.verifyingState}</p>
      </div>
    );
  }

  if (state === 'success') {
    // Book flow with a resolved order navigates in the effect above
    // and never reaches this branch, so `target` only needs to cover
    // the audio flow (and the book flow that arrived without a
    // resolvable order).
    const target = user ? computePostAuthRedirect(user) : '/';
    return (
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[color:var(--color-success)]/20 border border-[color:var(--color-success)]/40 mb-4">
          <Check className="w-8 h-8 text-[color:var(--color-success)]" strokeWidth={3} />
        </div>
        <h1 className="text-[22px] font-semibold text-dream-white mb-3">
          {STRINGS.auth.verify.successTitle}
        </h1>
        <p className="text-sm text-dream-white/90 leading-relaxed mb-6">
          {STRINGS.auth.verify.successBody}
        </p>
        <Link
          to={target}
          className="block w-full h-12 rounded-lg bg-dream-gold text-dream-navy text-base font-semibold flex items-center justify-center"
        >
          {STRINGS.auth.verify.backToApp}
        </Link>
      </div>
    );
  }

  const confirmUrl = userEmail
    ? `/auth/confirm?email=${encodeURIComponent(userEmail)}`
    : '/auth/confirm';

  return (
    <div className="text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[rgba(226,75,74,0.15)] border border-[rgba(226,75,74,0.4)] mb-4">
        <AlertCircle className="w-8 h-8 text-[#FCA5A5]" />
      </div>
      <h1 className="text-[22px] font-semibold text-dream-white mb-3">
        {STRINGS.auth.verify.errorTitle}
      </h1>
      <p className="text-sm text-dream-white/90 leading-relaxed mb-6">
        {STRINGS.auth.verify.errorBody}
      </p>
      <Button asChild className="w-full h-12 text-base font-semibold">
        <Link to={confirmUrl}>{STRINGS.auth.verify.requestNewLink}</Link>
      </Button>
      <div className="mt-4">
        <Link
          to="/auth/login"
          className="text-sm text-dream-white/80 underline hover:text-dream-white"
        >
          {STRINGS.auth.forgot.backToLogin}
        </Link>
      </div>
    </div>
  );
}
